import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'

import { EmptySearchResult } from '@/components/EmptySearchResult'
import InputSearch from '@/components/InputSearch'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  CommentTemplateCard,
  CommentTemplateDialog,
  DeleteCommentTemplateDialog,
} from '@/features/comment-template/components'
import {
  DEFAULT_COMMENT_TEMPLATE_LIMIT,
  DEFAULT_COMMENT_TEMPLATE_SORT,
  ICON_SIZE,
  INTERSECTION_OBSERVER_CONFIG,
  SKELETON_CARD_COUNT,
} from '@/features/comment-template/constants'
import { useCommentTemplateList } from '@/features/comment-template/hooks'
import { useCommentTemplateStore } from '@/features/comment-template/store'
import { useDebounce } from '@/hooks/useDebounce'
import NoCommentTemplateImage from '@/public/images/no-comment-template.svg'
import { SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

const CommentTemplateListPage: React.FC = () => {
  const { t } = useTranslation()
  const loadMoreRef = useRef<HTMLDivElement>(null)

  const {
    searchQuery,
    editingTemplate,
    dialogOpen,
    deletingTemplate,
    deleteDialogOpen,
    setSearchQuery,
    setEditingTemplate,
    setDialogOpen,
    setDeletingTemplate,
    setDeleteDialogOpen,
    reset,
  } = useCommentTemplateStore()

  const debouncedSearchQuery = useDebounce(
    searchQuery,
    SEARCH_DEBOUNCE_DELAY_MS
  )

  const trimmedSearchQuery = debouncedSearchQuery?.trim()

  const {
    data,
    isLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCommentTemplateList({
    q: trimmedSearchQuery || undefined,
    limit: DEFAULT_COMMENT_TEMPLATE_LIMIT,
    sort_by: DEFAULT_COMMENT_TEMPLATE_SORT,
  })

  const templates = useMemo(() => data?.pages.flat() ?? [], [data])

  useEffect(() => {
    return () => {
      reset()
    }
  }, [reset])

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0]
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage]
  )

  useEffect(() => {
    const targetElement = loadMoreRef.current
    if (!targetElement || !hasNextPage) return

    const observer = new IntersectionObserver(
      handleIntersection,
      INTERSECTION_OBSERVER_CONFIG
    )

    observer.observe(targetElement)

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection, hasNextPage])

  const handleAddNew = useCallback(() => {
    setEditingTemplate(null)
    setDialogOpen(true)
  }, [setEditingTemplate, setDialogOpen])

  const handleEdit = useCallback(
    (template: (typeof templates)[number]) => {
      setEditingTemplate(template)
      setDialogOpen(true)
    },
    [setEditingTemplate, setDialogOpen]
  )

  const handleDelete = useCallback(
    (template: (typeof templates)[number]) => {
      setDeletingTemplate(template)
      setDeleteDialogOpen(true)
    },
    [setDeletingTemplate, setDeleteDialogOpen]
  )

  const handleDialogClose = useCallback(
    (open: boolean) => {
      setDialogOpen(open)
      if (!open) {
        setEditingTemplate(null)
      }
    },
    [setDialogOpen, setEditingTemplate]
  )

  const handleDeleteDialogClose = useCallback(
    (open: boolean) => {
      setDeleteDialogOpen(open)
      if (!open) {
        setDeletingTemplate(null)
      }
    },
    [setDeleteDialogOpen, setDeletingTemplate]
  )

  const renderContent = () => {
    if (isLoading) {
      const skeletonItems = Array.from(
        { length: SKELETON_CARD_COUNT },
        (_, i) => ({
          id: `skeleton-${i}`,
        })
      )
      return (
        <div className="flex flex-col gap-4">
          {skeletonItems.map((item) => (
            <div
              key={item.id}
              className="h-32 border rounded-lg border-gray-light-200 bg-gray-light-50 animate-pulse"
            />
          ))}
        </div>
      )
    }

    if (templates.length === 0) {
      if (isFetching) {
        const skeletonItems = Array.from(
          { length: SKELETON_CARD_COUNT },
          (_, i) => ({
            id: `skeleton-${i}`,
          })
        )
        return (
          <div className="flex flex-col gap-4">
            {skeletonItems.map((item) => (
              <div
                key={item.id}
                className="h-32 border rounded-lg border-gray-light-200 bg-gray-light-50 animate-pulse"
              />
            ))}
          </div>
        )
      }

      return (
        <div className="flex flex-col items-center justify-center py-12">
          {trimmedSearchQuery ? (
            <EmptySearchResult translationKey="commentTemplate" />
          ) : (
            <>
              <img
                src={NoCommentTemplateImage}
                alt="No comment templates"
                className="mb-6"
              />
              <div className="mb-6 text-center">
                <h3 className="m-0 text-lg font-semibold text-gray-light-900">
                  {t('commentTemplate.empty.title')}
                </h3>
              </div>
              <Button
                onClick={handleAddNew}
                className="flex items-center gap-2 px-4 py-2"
                leadingIcon={<Plus size={ICON_SIZE.plus} />}
              >
                {t('commentTemplate.addNew')}
              </Button>
            </>
          )}
        </div>
      )
    }

    return (
      <>
        <div className="flex flex-col gap-4">
          {templates.map((template) => (
            <CommentTemplateCard
              key={template.id}
              template={template}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>

        {hasNextPage && (
          <div ref={loadMoreRef} className="flex justify-center py-4">
            {isFetchingNextPage && (
              <Spinner
                className={`w-${ICON_SIZE.spinner} h-${ICON_SIZE.spinner} text-blue-600`}
              />
            )}
          </div>
        )}
      </>
    )
  }

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="mb-0 text-2xl font-bold text-gray-light-900">
            {t('commentTemplate.title')}
          </h1>
          <p className="m-0 text-base text-gray-light-600">
            {t('commentTemplate.description')}
          </p>
        </div>

        {(templates.length > 0 || trimmedSearchQuery) && (
          <div className="flex items-center flex-shrink-0 gap-3">
            <InputSearch
              searchText={searchQuery}
              setSearchText={setSearchQuery}
              placeholder={t('commentTemplate.searchPlaceholder')}
              className="w-48"
            />
            <Button
              onClick={handleAddNew}
              variant="solid"
              color="primary"
              className="flex items-center text-base font-medium"
              leadingIcon={<Plus size={ICON_SIZE.plus} />}
            >
              {t('commentTemplate.addNew')}
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">{renderContent()}</div>

      <CommentTemplateDialog
        template={editingTemplate}
        open={dialogOpen}
        onOpenChange={handleDialogClose}
      />

      <DeleteCommentTemplateDialog
        template={deletingTemplate}
        open={deleteDialogOpen}
        onOpenChange={handleDeleteDialogClose}
      />
    </div>
  )
}

export default CommentTemplateListPage
